import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core'

import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeElementsService } from '../services/scope-elements.service'
import { UpdateRolePayload, ScopeElementsConfig } from '../models'
import { isPreferenceActive } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import {
  getCrFieldToDisplay,
  showCrDifferences,
  getDeltaValue,
  showPricingDeltaForConfig,
} from '../utils/change-request'
import { DepartmentWithChanges } from '../models/entities-with-changes/department-with-changes.interface'
import { FormatHoursPipe } from '@app/shared/pipe/formatHours.pipe'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { isNil } from 'lodash'
import { DEFAULT_PLACEHOLDER } from '@app/shared/constants/default-placeholder.const'
import { Money } from '@app/features/scope-overview/model/money.model'

@Component({
  selector: 'scope-departments-row',
  templateUrl: './departments-row.component.html',
  providers: [FormatHoursPipe],
})
export class DepartmentsRowComponent {
  @Input() level: number
  @Input() component: ScopeComponent
  @Input() config: ScopeElementsConfig
  @Input() department: DepartmentWithChanges

  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()

  constructor(
    protected scopeElementsService: ScopeElementsService,
    private formatHoursPipe: FormatHoursPipe,
    private convertCurrencyPipe: ConvertCurrencyPipe
  ) {}

  selectDepartment(departmentId: number) {
    this.scopeElementsService.toggleDepartment(departmentId)
  }

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay<DepartmentWithChanges>(fieldName, this.department)
  showDiff = (fieldName: string): boolean =>
    showCrDifferences<DepartmentWithChanges>(this.config, this.department, fieldName)

  formatHours = (value: number): string => this.formatHoursPipe.transform(value)
  formatPrice = (value: number | Money): string => {
    const amount = value instanceof Money ? value.amount : value
    return isNil(value) ? DEFAULT_PLACEHOLDER : this.convertCurrencyPipe.transform(amount, this.config?.currency)
  }

  get showPricingDelta(): boolean {
    return showPricingDeltaForConfig(this.config)
  }

  getDepartmentHoursDelta(): number {
    return getDeltaValue(this.department, '_currentHours', this.department.getTotalRateCardHours(), 'oldHours')
  }

  getDepartmentPriceDelta(): number {
    return getDeltaValue(
      this.department,
      '_currentSellingPrice',
      this.department.getTotalSellingPrice({ markup: this.component.costPlusValue })?.amount,
      'oldSellingPrice'
    )
  }

  @HostBinding('class.change-request-removed') get isChangeRequestRemoved() {
    return (
      this.config?.isChangeRequest && (this.config?.showDiff || this.config?.showNetDelta) && this.department.isRemoved
    )
  }

  @HostBinding('class.change-request-added') get isChangeRequestAdded() {
    return (
      this.config?.isChangeRequest &&
      (this.config?.showDiff || this.config?.showNetDelta) &&
      (this.department.isAdded || !this.department.id)
    )
  }

  protected readonly isPreferenceActive = isPreferenceActive
  protected readonly DEFAULT_PLACEHOLDER = DEFAULT_PLACEHOLDER
}
